import { ChatAdjustmentCTA, ChatAdjustmentMealContent, SuggestedTaskItem } from '@demind-inc/core';
import dayjs from 'dayjs';

import { AssistantChatMessage, ChatSuggestedEvent, UnifiedChatRichContextEntry } from '../types';

import { transformHHmmToTargetDate } from './date.helpers';
import { SESSION_EMOJI } from './constants';
import { SyncChatResultOutput } from '../api';
import { DEFAULT_TASK_DURATION_MIN } from '../constants';
import { SLEEP_AFTER_MIDNIGHT_HOUR } from '../../helpers/allPlanScheduleDate.helpers';

export const TEMPORAL_EVENT_PREFIX = 'temporal-';

/** Identity key for suggestion-card UI state (processed/loading/dismissed).
 * Falls back to eventId for blocks that don't need disambiguation — only
 * meal/sleep set blockInstanceId (shared eventId across cards). Matches
 * lifestack_calendar. */
export const getSuggestedBlockKey = (block: ChatSuggestedEvent): string =>
  block.blockInstanceId ?? block.eventId;

const getRichContextContentType = (richContext: unknown): string => {
  if (!richContext || typeof richContext !== 'object' || Array.isArray(richContext)) {
    return '';
  }

  const content = (richContext as Record<string, unknown>).content;
  if (!content) return '';

  if (Array.isArray(content)) {
    for (const item of content) {
      if (!item || typeof item !== 'object') continue;
      const type = typeof (item as Record<string, unknown>).type === 'string'
        ? ((item as Record<string, unknown>).type as string).trim().toLowerCase()
        : '';
      if (type) return type;
    }
    return '';
  }

  if (typeof content !== 'object') return '';
  const type = (content as Record<string, unknown>).type;
  return typeof type === 'string' ? type.trim().toLowerCase() : '';
};

const isWorkoutPlanRichContext = (richContext: unknown): boolean =>
  getRichContextContentType(richContext) === 'workout_plan';

const parseHHmm = (time: string) => {
  const [hourRaw, minuteRaw] = time.split(':').map(Number);
  return { hour: hourRaw, minute: minuteRaw };
};

/**
 * Sleep suggestions describe the upcoming sleep window for the selected plan date.
 * Evening bedtimes start on the selected date; after-midnight bedtimes start on the
 * next calendar date but still belong to the selected date's night.
 */
export const resolveSleepSuggestionDateTimes = (
  sleepTime: string,
  wakeTime: string,
  targetDate: string
) => {
  const sleepDate = dayjs(targetDate).startOf('day');
  const { hour: sleepHour, minute: sleepMinute } = parseHHmm(sleepTime);
  const { hour: wakeHour, minute: wakeMinute } = parseHHmm(wakeTime);

  const baseDate =
    sleepHour < SLEEP_AFTER_MIDNIGHT_HOUR ? sleepDate.add(1, 'day') : sleepDate;

  const startTime = baseDate.hour(sleepHour).minute(sleepMinute).second(0).millisecond(0);
  let endTime = baseDate.hour(wakeHour).minute(wakeMinute).second(0).millisecond(0);

  if (endTime.isBefore(startTime)) {
    endTime = endTime.add(1, 'day');
  }

  return { startTime, endTime };
};

export const hasWorkoutPlanRichContextEntry = (
  entries?: UnifiedChatRichContextEntry[]
): boolean => entries?.some((entry) => isWorkoutPlanRichContext(entry.richContext)) ?? false;

const isWorkoutLikeScheduleName = (name: string): boolean => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return false;

  return (
    /^(workout|exercise|gym|training)\b/.test(normalized) ||
    /\bworkout\b/.test(normalized) ||
    /\bexercise\b/.test(normalized)
  );
};

const isWorkoutScheduleSuggestedBlock = (block: ChatSuggestedEvent): boolean => {
  if (block.action !== 'setEvent') return false;
  if (block.autoScheduleFrom?.from === 'meal' || block.autoScheduleFrom?.from === 'sleep') {
    return false;
  }
  if (block.autoScheduleFrom?.from === 'todo') return false;

  if (block.categoryId === 'default_exercise') return true;

  const name = typeof block.name === 'string' ? block.name : '';
  return isWorkoutLikeScheduleName(name);
};

export const filterWorkoutScheduleBlocksWithRichContextCtas = (
  blocks: ChatSuggestedEvent[],
  richContextEntries?: UnifiedChatRichContextEntry[]
): ChatSuggestedEvent[] => {
  if (!hasWorkoutPlanRichContextEntry(richContextEntries)) {
    return blocks;
  }

  return blocks.filter((block) => !isWorkoutScheduleSuggestedBlock(block));
};

export const collectRichContextEntriesNearMessage = (
  messages: AssistantChatMessage[],
  messageIndex: number
): UnifiedChatRichContextEntry[] => {
  const entries: UnifiedChatRichContextEntry[] = [];
  const current = messages[messageIndex];

  if (current?.richContexts?.length) {
    entries.push(...current.richContexts);
  }

  const previous = messages[messageIndex - 1];
  if (previous?.role === 'assistant' && previous.richContexts?.length) {
    entries.push(...previous.richContexts);
  }

  return entries;
};

export const getLastAssistantMessageIndex = (
  messages: AssistantChatMessage[]
): number | undefined => {
  return messages
    .map((msg, idx) => ({ msg, idx }))
    .filter(({ msg }) => msg.role === 'assistant')
    .pop()?.idx;
};

export const getNumOfAssistantMessages = (messages: AssistantChatMessage[]): number => {
  return messages.filter((msg) => msg.role === 'assistant').length;
};

export const containSuggestedBlocksInMessages = (
  messages: AssistantChatMessage[],
  suggestedEvents: ChatSuggestedEvent[]
) => {
  const lastAssistantIndex = getLastAssistantMessageIndex(messages);
  if (lastAssistantIndex === undefined) {
    return messages;
  }
  const newMessages = [...messages];
  newMessages[lastAssistantIndex].suggestedBlocks = suggestedEvents;

  return newMessages;
};

export const transformSuggestedTasksToEvents = (
  suggestedTasks: SuggestedTaskItem[],
  date: string,
  chatSessionId?: string | null
) => {
  return suggestedTasks.map((task) => {
    const startDayOffset = task.suggestedStartDayOffset ?? 0;
    const endDayOffset = task.suggestedEndDayOffset ?? 0;
    const startBaseDay = dayjs(date).startOf('day').add(startDayOffset, 'day');
    const endBaseDay = dayjs(date).startOf('day').add(endDayOffset, 'day');

    return {
      eventId: `${TEMPORAL_EVENT_PREFIX}${task.taskId}`,
      linkedMessageId: task.taskId,
      name: task.name,
      startTime: transformHHmmToTargetDate(task.suggestedStartTime, startBaseDay).toISOString(),
      endTime: transformHHmmToTargetDate(task.suggestedEndTime, endBaseDay).toISOString(),
      description: task.reasoning,
      autoScheduleFrom: {
        from: 'todo',
        id: task.taskId,
        chatSessionId,
        predicted: true,
      },
      taskFrom: {
        taskId: task.taskId,
        boardId: task.boardId,
        from: task.appFrom,
        autoSuggestedReason: task.reasoning,
      },
      isNew: true,
      action: 'setEvent',
    } as ChatSuggestedEvent;
  });
};

export const transformAdjustmentCTAToSuggestedBlocks = (
  adjustmentCTAs: ChatAdjustmentCTA[]
): ChatSuggestedEvent[] => {
  const blocks: ChatSuggestedEvent[] = [];

  adjustmentCTAs.forEach((cta) => {
    // Meals
    if (cta.meals) {
      cta.meals.forEach((mealChange, idx) => {
        const before = mealChange.before;
        const after = mealChange.after;
        if (after) {
          const mealDate = dayjs(after.startTime).format('YYYY-MM-DD');

          blocks.push({
            eventId: `${TEMPORAL_EVENT_PREFIX}meal-${after.mealType}-${mealDate}-${after.startTime!}-${after.endTime!}-${idx}`,
            name: `${SESSION_EMOJI.meal} ${after.mealType}`,
            description: after.description!,
            startTime: after.startTime!,
            endTime: after.endTime!,
            ...(before && {
              before: {
                mealType: before.mealType!,
                startTime: before.startTime!,
                endTime: before.endTime!,
              },
            }),
            autoScheduleFrom: {
              from: 'meal',
              id: after.mealType || '',
              predicted: true,
            },
            isNew: !before,
            action: 'setEvent',
          });
        }
      });
    }

    // Sleep
    if (cta.sleep) {
      const before = cta.sleep.before;
      const after = cta.sleep.after;
      if (after) {
        blocks.push({
          eventId: `${TEMPORAL_EVENT_PREFIX}sleep-${after.sleepTime}-${after.wakeTime}`,
          name: `${SESSION_EMOJI.sleep} Sleep`,
          description: after.description!,
          startTime: after.sleepTime!,
          endTime: after.wakeTime!,
          ...(before && {
            before: {
              startTime: before.sleepTime!,
              endTime: before.wakeTime!,
            },
          }),
          autoScheduleFrom: {
            from: 'sleep',
            id: after.sleepTime || '',
            predicted: true,
          },
          action: 'setEvent',
          isNew: !before,
        });
      }
    }

    // Schedule
    if (cta.schedule) {
      cta.schedule.forEach((schedChange, idx) => {
        const before = schedChange.before;
        const after = schedChange.after;
        if (after) {
          const scheduleDate = dayjs(after.startDate).format('YYYY-MM-DD');

          blocks.push({
            eventId: after.id
              ? after.id
              : `${TEMPORAL_EVENT_PREFIX}schedule-${scheduleDate}-${after.startDate!}-${after.endDate!}-${idx}`,
            name: `${after.name}`,
            startTime: after.startDate!,
            endTime: after.endDate!,
            description: after.description,
            categoryId: after.categoryId,
            ...(((after as { recurrenceString?: string | null }).recurrenceString ??
              (after as { recurrence?: { string?: string | null } }).recurrence?.string) && {
              recurrence: {
                string:
                  (after as { recurrenceString?: string | null }).recurrenceString ??
                  (after as { recurrence?: { string?: string | null } }).recurrence?.string,
              },
            }),
            ...(before && {
              before: {
                startTime: before.startDate!,
                endTime: before.endDate!,
                name: before.name,
                categoryId: before.categoryId,
              },
            }),
            taskFrom: after.todoFrom,
            autoScheduleFrom: {
              from: after.todoFrom ? 'todo' : 'aiScheduler',
              id: after.id || '',
              predicted: true,
            },
            isNew: !before,
            action: 'setEvent',
          });
        } else if (before && !after) {
          // Handle deletion: after is undefined but before exists
          const scheduleDate = dayjs(before.startDate).format('YYYY-MM-DD');
          blocks.push({
            eventId: before.id
              ? before.id
              : `${TEMPORAL_EVENT_PREFIX}schedule-${scheduleDate}-${before.startDate!}-${before.endDate!}-${idx}`,
            name: before.name || 'Schedule',
            startTime: before.startDate!,
            endTime: before.endDate!,
            before: {
              startTime: before.startDate!,
              endTime: before.endDate!,
              name: before.name,
              categoryId: before.categoryId,
            },
            ...(before.todoFrom && {
              taskFrom: before.todoFrom,
            }),
            autoScheduleFrom: {
              from: before.todoFrom ? 'todo' : 'aiScheduler',
              id: before.id || '',
              predicted: true,
            },
            action: 'deleteEvent',
          });
        }
      });
    }

    // Tasks
    if (cta.tasks) {
      cta.tasks.forEach((tasksChange, idx) => {
        const before = tasksChange.before;
        const after = tasksChange.after;
        if (after) {
          // Check if this is a deletion: after.deleted is true and before.deleted is false or undefined
          if (
            after.deleted === true &&
            (before?.deleted === false || before?.deleted === undefined)
          ) {
            // Handle task deletion
            const taskDate = before?.date || after.date || dayjs().format('YYYY-MM-DD');
            const startTime = before?.startTime || after.startTime || dayjs().toISOString();
            const endTime = before?.endTime || after.endTime || dayjs().toISOString();
            blocks.push({
              eventId: `${TEMPORAL_EVENT_PREFIX}tasks-delete-${
                before?.taskId || after.taskId || after.name
              }-${taskDate}`,
              name: before?.name || after.name || 'Task',
              startTime,
              endTime,
              date: taskDate,
              durationMin:
                before?.durationMinutes || after.durationMinutes || DEFAULT_TASK_DURATION_MIN,
              before: {
                startTime: before?.startTime,
                endTime: before?.endTime,
                name: before?.name,
                date: before?.date,
              },
              taskFrom: before
                ? {
                    taskId: before.taskId!,
                    boardId: before.boardId!,
                    from: before.appFrom!,
                  }
                : undefined,
              autoScheduleFrom: {
                from: 'todo',
                id: before?.taskId || after.taskId || '',
                predicted: true,
              },
              isNew: false,
              action: 'deleteTask',
            });
          } else {
            blocks.push({
              eventId: `${TEMPORAL_EVENT_PREFIX}tasks-${after.taskId || after.name}-${after.date}-${after.startTime!}-${after.endTime!}-${idx}`,
              name: `${after.name}`,
              startTime: after.startTime,
              endTime: after.endTime,
              date: after.date,
              durationMin: after.durationMinutes || DEFAULT_TASK_DURATION_MIN,
              ...(before && {
                before: {
                  startTime: before.startTime,
                  endTime: before.endTime,
                  name: before.name,
                  date: before.date,
                },
              }),
              ...(before && {
                taskFrom: {
                  taskId: before.taskId,
                  boardId: before.boardId,
                  from: before.appFrom,
                },
              }),
              autoScheduleFrom: {
                from: 'todo',
                id: after.taskId || '',
                predicted: true,
              },
              isNew: !before,
              action: 'setTask',
            });
          }
        }
      });
    }

    // Routines
    if (cta.routines) {
      cta.routines.forEach((routineChange, idx) => {
        const before = routineChange.before;
        const after = routineChange.after;
        if (after) {
          // Convert startTime and endTime to ISO strings if they're in HH:mm format
          const targetDate = dayjs().format('YYYY-MM-DD');
          const startTime = after.startTime;
          const endTime = after.endTime;

          if (!startTime || !endTime) {
            return;
          }

          const startTimeISO = dayjs(startTime).isValid()
            ? dayjs(startTime).toISOString()
            : dayjs(`${targetDate} ${startTime}`).toISOString();
          const endTimeISO = dayjs(endTime).isValid()
            ? dayjs(endTime).toISOString()
            : dayjs(`${targetDate} ${endTime}`).toISOString();

          // Get activity ID from after or before (for updates)
          const activityId = after?.routineActivityId || before?.routineActivityId;

          blocks.push({
            eventId: `${TEMPORAL_EVENT_PREFIX}routines-${activityId || after.name || 'routine'}-${targetDate}-${startTime}-${endTime}-${idx}`,
            name: after.name || 'Routine',
            startTime: startTimeISO,
            endTime: endTimeISO,
            durationMin: after.durationMin,
            recurring: after.recurring,
            ...(before && {
              before: {
                startTime: before.startTime,
                endTime: before.endTime,
                name: before.name,
              },
            }),
            autoScheduleFrom: {
              from: 'routine',
              id: activityId,
              predicted: true,
            },
            isNew: !before,
            action: 'setRoutine',
          });
        } else if (before) {
          // Handle deletion: after is undefined but before exists
          const targetDate = dayjs().format('YYYY-MM-DD');
          const startTime = before.startTime;
          const endTime = before.endTime;

          if (!startTime || !endTime) {
            return;
          }

          const startTimeISO = dayjs(startTime).isValid()
            ? dayjs(startTime).toISOString()
            : dayjs(`${targetDate} ${startTime}`).toISOString();
          const endTimeISO = dayjs(endTime).isValid()
            ? dayjs(endTime).toISOString()
            : dayjs(`${targetDate} ${endTime}`).toISOString();

          // Get activity ID from before
          const activityId = before.routineActivityId;

          blocks.push({
            eventId: `${TEMPORAL_EVENT_PREFIX}routines-${activityId || before.name || 'routine'}-${targetDate}-${startTime}-${endTime}-${idx}`,
            name: before.name || 'Routine',
            startTime: startTimeISO,
            endTime: endTimeISO,
            before: {
              startTime: before.startTime,
              endTime: before.endTime,
              name: before.name,
            },
            autoScheduleFrom: {
              from: 'routine',
              id: activityId,
              predicted: true,
            },
            action: 'deleteRoutine',
          });
        }
      });
    }
  });

  return blocks;
};

export const transformSleepMealToSuggestedBlocks = (
  sleepData?: SyncChatResultOutput['sleepData'],
  mealData?: SyncChatResultOutput['mealData'],
  date?: string
): ChatSuggestedEvent[] => {
  const blocks: ChatSuggestedEvent[] = [];
  const targetDate = date || dayjs().format('YYYY-MM-DD');

  // Transform sleep data
  if (sleepData?.sleepTime && sleepData?.wakeTime) {
    const { startTime, endTime } = resolveSleepSuggestionDateTimes(
      sleepData.sleepTime,
      sleepData.wakeTime,
      targetDate
    );

    blocks.push({
      eventId: `${TEMPORAL_EVENT_PREFIX}sleep-${sleepData.sleepTime}-${sleepData.wakeTime}`,
      name: `${SESSION_EMOJI.sleep} Sleep`,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      autoScheduleFrom: {
        from: 'sleep',
        id: sleepData.sleepTime,
        predicted: true,
      },
      isNew: true,
      action: 'checkOnCalendar',
    });
  }

  // Transform meal data
  if (mealData) {
    const meals = [
      { type: 'breakfast', data: mealData.breakfast },
      { type: 'lunch', data: mealData.lunch },
      { type: 'dinner', data: mealData.dinner },
    ];

    meals.forEach((meal, idx) => {
      if (meal.data?.start && meal.data?.end) {
        blocks.push({
          eventId: `${TEMPORAL_EVENT_PREFIX}meal-${meal.type}-${targetDate}-${meal.data.start!}-${meal.data.end!}-${idx}`,
          name: `${SESSION_EMOJI.meal} ${meal.type.charAt(0).toUpperCase() + meal.type.slice(1)}`,
          startTime: dayjs(`${targetDate} ${meal.data.start}`).toISOString(),
          endTime: dayjs(`${targetDate} ${meal.data.end}`).toISOString(),
          autoScheduleFrom: {
            from: 'meal',
            id: meal.type,
            predicted: true,
          },
          isNew: true,
          mealContent: meal.data.content as ChatAdjustmentMealContent,
          action: 'checkOnCalendar',
        });
      }
    });
  }

  return blocks;
};
